/* The business journey, in the order an operator lives it. Every module or feature list on
   the marketing site, and its JSON-LD, is ordered by this file. Pure data: icons live in
   apps/web/src/lib/marketing/journey-v1.tsx. */
export const JOURNEY_STAGES = [
  {
    key: "hiring",
    label: "Hiring",
    headline: "Staff the building",
    description:
      "Post jobs, move applicants through a pipeline and send offers, then onboard with HR documents and training that issues its own certificates.",
  },
  {
    key: "sales",
    label: "Sales",
    headline: "Fill the building",
    description:
      "Every inquiry captured: a visual lead pipeline, tours, campaigns and referral sources, with an AI front desk answering the phone after hours.",
  },
  {
    key: "admission",
    label: "Admission",
    headline: "Move them in",
    description:
      "E-signed admission packets, ask-once intake and a Move-In Command Center whose steps verify themselves as paperwork, orders and billing actually land.",
  },
  {
    key: "care",
    label: "Care",
    headline: "Care for them",
    description:
      "Resident records, care plans with signing teams, vitals, incidents, activities and therapy EMR: one chart the whole team works from.",
  },
  {
    key: "pharmacy",
    label: "Pharmacy",
    headline: "Pass the meds",
    description:
      "A full eMAR: physician orders, tap-to-administer passes, documented holds and refusals, plus pharmacy receiving, refills and supply forecasting.",
  },
  {
    key: "billing",
    label: "Billing",
    headline: "Get paid",
    description:
      "Charges become invoices, families pay online by ACH or card, autopay runs itself, and rent roll, AR, expenses and P&L stay current on their own.",
  },
  {
    key: "compliance",
    label: "Compliance",
    headline: "Stay inspection-ready",
    description:
      "Medicaid and MCO authorizations with expiring-auth alerts, daily roll, checklists, policies and an audit trail behind every signature.",
  },
  {
    key: "maintenance",
    label: "Maintenance",
    headline: "Keep it running",
    description:
      "Work that isn't clinical still gets tracked: a cross-facility task center, transport, vendors and expenses, so nothing about the building falls through.",
  },
] as const;

export type JourneyStageKey = (typeof JOURNEY_STAGES)[number]["key"];

/** Position of a stage, for sorting arbitrary lists into journey order. */
const JOURNEY_RANK: Record<string, number> = Object.fromEntries(
  JOURNEY_STAGES.map((s, i) => [s.key, i]),
);

/** "Hiring: Staff the building", … — the featureList both schemas publish. */
export const JOURNEY_FEATURE_LIST = JOURNEY_STAGES.map((s) => `${s.label}: ${s.headline}`);

/** Order any list that carries a journey stage key. */
export function byJourney<T>(items: readonly T[], stageOf: (item: T) => string): T[] {
  // oxlint-disable-next-line unicorn/no-array-sort -- a fresh array; the phone app's Hermes has no toSorted
  return [...items].sort((a, b) => {
    const ra = JOURNEY_RANK[stageOf(a)] ?? JOURNEY_STAGES.length;
    const rb = JOURNEY_RANK[stageOf(b)] ?? JOURNEY_STAGES.length;
    return ra - rb;
  });
}
